import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import maplibregl from 'maplibre-gl';
import type { Place, WingEvent } from '@wingman/shared';
import MapView, { type MapVisualMode } from '../components/map/MapView.js';
import EventMarkers from '../components/map/EventMarkers.js';
import SearchBar from '../components/search/SearchBar.js';
import PlaceCard from '../components/place/PlaceCard.js';
import PlaceDetails from '../components/place/PlaceDetails.js';
import EventDetails from '../components/event/EventDetails.js';
import TopBar from '../components/layout/TopBar.js';
import CategoryChipBar from '../components/command/CategoryChipBar.js';
import TimeOfDayBadge from '../components/map/TimeOfDayBadge.js';
import BriefingCard from '../components/command/BriefingCard.js';
import GapPanel from '../components/command/GapPanel.js';
import TimelineScrubber from '../components/command/TimelineScrubber.js';
import WeatherOverlay from '../components/map/WeatherOverlay.js';
import { useTimeOfDayStore } from '../store/timeOfDayStore.js';
import { useMapStore } from '../store/mapStore.js';
import { useCommandStore } from '../store/commandStore.js';
import { useIsMobile } from '../hooks/useMediaQuery.js';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts.js';
import { api } from '../lib/api.js';
import { getWeather } from '../lib/weather.js';

export default function MapPage() {
  const {
    places,
    selectedPlace,
    loadingPlaces,
    fetchError,
    activeCategory,
    bbox,
    filters,
    mapTheme,
    flyToTarget,
    center,
    setBBox,
    setPlaces,
    setSelectedPlace,
    setLoadingPlaces,
    setFetchError,
    setActiveCategory,
    setFlyToTarget,
  } = useMapStore();

  // Fix: reactive subscription to timelineCursor (was calling getState() in JSX)
  const timelineCursor = useCommandStore((s) => s.timelineCursor);
  const { timelineMode, highlightedPlaceId, vehicleProgress, setHighlightedPlaceId, setVehicleProgress } = useCommandStore();

  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const timePhase = useTimeOfDayStore((s) => s.phaseOverride || s.phase);
  const [visualMode, setVisualMode] = useState<MapVisualMode>('raw');
  const [placeTotal, setPlaceTotal] = useState(0);
  const [weatherIcon, setWeatherIcon] = useState<string | undefined>(undefined);
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<WingEvent | null>(null);
  const requestController = useRef<AbortController | null>(null);

  useKeyboardShortcuts({
    onSearchFocus: () => {
      const input = document.querySelector('.search-bar input') as HTMLInputElement | null;
      input?.focus();
    },
    onZoomIn: () => {
      const mapEl = document.querySelector('.maplibregl-map') as HTMLElement & { __maplibreMap?: unknown } | null;
      if (mapEl && (mapEl.__maplibreMap as { zoomIn?: () => void } | undefined)?.zoomIn) {
        (mapEl.__maplibreMap as { zoomIn: () => void }).zoomIn();
      }
    },
    onZoomOut: () => {
      const mapEl = document.querySelector('.maplibregl-map') as HTMLElement & { __maplibreMap?: unknown } | null;
      if (mapEl && (mapEl.__maplibreMap as { zoomOut?: () => void } | undefined)?.zoomOut) {
        (mapEl.__maplibreMap as { zoomOut: () => void }).zoomOut();
      }
    },
    onToggleTheme: () => {},
    onEscape: () => { setSelectedPlace(null); setSelectedEvent(null); },
    onGoHome: () => navigate('/'),
    onTogglePlayback: () => {
      const state = useCommandStore.getState();
      if (state.timelineCursor >= 1 && !state.timelinePlaying) state.setTimelineCursor(0);
      state.setTimelinePlaying(!state.timelinePlaying);
    },
  });

  // ── Fetch places when bbox / filters change ──────────────────────────────
  const loadPlaces = useCallback(
    async (newBbox: typeof bbox) => {
      if (!newBbox) return;
      setLoadingPlaces(true);
      setFetchError(null);
      requestController.current?.abort();
      const controller = new AbortController();
      requestController.current = controller;
      try {
        const result = await api.places.getByBBox(newBbox, {
          category: activeCategory || undefined,
          openNow: filters.openNow,
          minRating: filters.minRating,
          sortBy: filters.sortBy,
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setPlaces(result.data);
          setPlaceTotal(result.total);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setPlaces([]);
          setPlaceTotal(0);
          setFetchError(
            error instanceof Error && error.message !== 'Failed to fetch'
              ? error.message
              : 'Could not load places. Check your connection.',
          );
        }
      } finally {
        if (!controller.signal.aborted) setLoadingPlaces(false);
      }
    },
    [activeCategory, filters, setPlaces, setLoadingPlaces, setFetchError],
  );

  useEffect(() => {
    if (bbox) loadPlaces(bbox);
    return () => requestController.current?.abort();
  }, [bbox, loadPlaces]);

  // ── Live weather → drives the map colour grade (shares the 10-min cache
  //    with WeatherOverlay, so this adds no extra network traffic) ──────────
  useEffect(() => {
    let active = true;
    getWeather(center.lat, center.lng).then((w) => {
      if (active) setWeatherIcon(w?.iconKey);
    });
    return () => { active = false; };
  }, [center.lat, center.lng]);

  // ── Place selection: always fetch the full record (photos + complete tags) ─
  const handlePlaceClick = useCallback(
    async (place: Place) => {
      // Immediately show the panel with what we have (fast feedback)
      setSelectedPlace(place);
      setFlyToTarget({ lat: place.lat, lng: place.lng, zoom: 16 });

      // Then silently enrich with the full record from the API
      try {
        const full = await api.places.getById(place.id);
        setSelectedPlace(full.data);
      } catch {
        // Keep the partial place already shown — silently degrade
      }
    },
    [setSelectedPlace, setFlyToTarget],
  );

  const handlePlaceHighlight = useCallback(
    (place: Place | null) => {
      setHighlightedPlaceId(place?.id || null);
      if (place) setFlyToTarget({ lat: place.lat, lng: place.lng, zoom: 16 });
    },
    [setHighlightedPlaceId, setFlyToTarget],
  );

  const handleEventClick = useCallback(
    (event: WingEvent) => {
      setSelectedEvent(event);
      setFlyToTarget({ lat: event.lat, lng: event.lng, zoom: 16 });
    },
    [setFlyToTarget],
  );

  const handleVehicleProgress = useCallback(
    (progress: number) => setVehicleProgress(progress),
    [setVehicleProgress],
  );

  const handleHourChange = useCallback((hour: number) => {
    const phase = hour < 6 ? 'night' : hour < 10 ? 'dawn' : hour < 17 ? 'day' : hour < 20 ? 'dusk' : 'night';
    useTimeOfDayStore.getState().setPhaseOverride(phase);
  }, []);

  // Fix: derive the weather hour reactively from the Zustand-subscribed timelineCursor
  const weatherHour = Math.floor(timelineCursor * 23);

  const showSidebar = !selectedPlace && places.length > 0 && !isMobile;
  const showMobileSheet = !selectedPlace && places.length > 0 && isMobile;

  return (
    <div className="relative h-full w-full overflow-hidden">
      <div className={`system-grid system-grid-${visualMode}`} aria-hidden="true" />
      <div className={`atmosphere-overlay atmosphere-${timePhase}`} aria-hidden="true" />

      <div className="system-status" aria-label="System status">
        <span>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--green)', display: 'inline-block', marginRight: 5 }} />
          OSM_NET
        </span>
        <strong>LIVE</strong>
      </div>

      <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer"
        className="absolute bottom-3 right-3 z-10 text-[10px] text-cyan-100/70 hover:text-cyan-100">
        © OpenStreetMap contributors
      </a>
      <a href="#map-canvas"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:bg-white focus:text-brand-600 focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg">
        Skip to map
      </a>

      <MapView
        key={visualMode}
        places={places}
        selectedPlace={selectedPlace}
        activeCategory={activeCategory}
        onPlaceClick={handlePlaceClick}
        onMapMove={(bbox, zoom) => setBBox(bbox, zoom)}
        visualMode={visualMode}
        mapTheme={mapTheme}
        flyToTarget={flyToTarget}
        onFlyToComplete={() => setFlyToTarget(null)}
        highlightedPlaceId={highlightedPlaceId}
        vehicleProgress={timelineMode === 'route' ? vehicleProgress : 0}
        timePhase={timePhase as import('../lib/timeOfDay.js').TimeOfDayPhase}
        weatherIcon={weatherIcon}
        onMapReady={setMapInstance}
      />

      <EventMarkers map={mapInstance} bbox={bbox} onEventClick={handleEventClick} />

      <SearchBar />
      <CategoryChipBar activeCategory={activeCategory} onCategoryChange={setActiveCategory} />
      <TopBar visualMode={visualMode} onVisualModeChange={setVisualMode} />
      <BriefingCard places={places} />
      {!isMobile && <GapPanel onFlyTo={(lat, lng) => setFlyToTarget({ lat, lng, zoom: 15 })} />}
      <WeatherOverlay lat={center.lat} lng={center.lng} hour={weatherHour} />

      <div className="system-telemetry hidden md:flex">
        <TimeOfDayBadge />
        <span className="telemetry-divider" />
        <span className="mono">OBJECTS <strong>{String(placeTotal).padStart(3, '0')}</strong></span>
      </div>

      {/* ── Error banner ── */}
      {fetchError && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          style={{
            position: 'absolute',
            top: 'calc(var(--topbar-h) + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 30,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '9px 16px',
            background: 'rgba(248,81,73,0.12)',
            border: '1px solid rgba(248,81,73,0.4)',
            borderRadius: 6,
            backdropFilter: 'blur(12px)',
            color: 'var(--red)',
            fontSize: 12,
            fontWeight: 500,
            maxWidth: 'min(480px, calc(100vw - 32px))',
          }}
          role="alert"
          aria-live="assertive"
        >
          <AlertTriangle size={14} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1 }}>{fetchError}</span>
          <button
            type="button"
            onClick={() => { setFetchError(null); if (bbox) loadPlaces(bbox); }}
            style={{
              marginLeft: 8,
              padding: '2px 10px',
              border: '1px solid rgba(248,81,73,0.5)',
              borderRadius: 4,
              background: 'transparent',
              color: 'var(--red)',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
          <button
            type="button"
            onClick={() => setFetchError(null)}
            style={{ color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
            aria-label="Dismiss error"
          >
            <X size={13} />
          </button>
        </motion.div>
      )}

      {/* ── Loading indicator ── */}
      {loadingPlaces && (
        <div
          style={{
            position: 'absolute',
            bottom: 24,
            left: 16,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 14px',
            background: 'rgba(22,27,34,0.92)',
            border: '1px solid var(--border)',
            backdropFilter: 'blur(12px)',
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--text-muted)',
          }}
          role="status"
          aria-live="polite"
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', flexShrink: 0, animation: 'hud-ping 1s ease-in-out infinite' }} />
          Scanning sector…
        </div>
      )}

      {/* ── Desktop sidebar ── */}
      <AnimatePresence>
        {showSidebar && (
          <motion.div
            initial={{ opacity: 0, x: -14 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -14 }}
            transition={{ duration: 0.22 }}
            className="absolute z-10 sidebar w-80"
            style={{
              top: 'calc(var(--topbar-h) + 110px)',
              left: 12,
              maxHeight: 'calc(100vh - var(--topbar-h) - 120px)',
            }}
            role="navigation"
            aria-label="Places list"
          >
            {/* Column header with total count */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 80px 52px',
                padding: '7px 12px',
                borderBottom: '1px solid var(--border)',
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'var(--text-dim)',
              }}
            >
              <span>
                {placeTotal > places.length
                  ? `${places.length.toLocaleString()} of ${placeTotal.toLocaleString()}`
                  : `${places.length.toLocaleString()} place${places.length !== 1 ? 's' : ''}`}
              </span>
              <span>Category</span>
              <span style={{ textAlign: 'right' }}>Rating</span>
            </div>
            {places.map((place) => (
              <PlaceCard key={place.id} place={place} onClick={handlePlaceClick} />
            ))}
          </motion.div>
        )}

        {/* ── Mobile bottom sheet ── */}
        {showMobileSheet && (
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bottom-sheet bottom-sheet-collapsed z-20"
            role="navigation"
            aria-label="Places list"
          >
            <div className="bottom-sheet-handle" />
            <div
              style={{
                padding: '10px 14px',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {placeTotal > places.length
                ? `${places.length} of ${placeTotal.toLocaleString()} places`
                : `${places.length} places`}
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: '50vh' }}>
              {places.map((place) => (
                <PlaceCard key={place.id} place={place} onClick={handlePlaceClick} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {selectedPlace && (
        <PlaceDetails place={selectedPlace} onClose={() => setSelectedPlace(null)} />
      )}

      {selectedEvent && (
        <EventDetails event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}

      <TimelineScrubber
        places={places}
        routeGeometry={null}
        onPlaceHighlight={handlePlaceHighlight}
        onVehicleProgress={handleVehicleProgress}
        onHourChange={handleHourChange}
      />
    </div>
  );
}
